import { useAuth } from '../../../auth/AuthProvider';
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { Search, EventRepeat, EventBusyOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { useAgendaTranslation as useTranslation } from '../translations';
import dayjs from 'dayjs';
import {
    fetchAppointments,
    getAppointmentClinicians,
} from '../../../services/appointments';
import { getAgendaBounds, groupAppointments } from '../agendaUtils';
import AppointmentDialogs, { statusLabels } from './AppointmentDialogs';

const filterStates = {
    active: ['SCHEDULED', 'ONGOING'],
    completed: ['COMPLETED'],
    cancelled: ['CANCELLED']
};
const colors = {
    SCHEDULED: ['#CBE2FF', '#1976D2'],
    ONGOING: ['#FFF2D9', '#B86E00'],
    COMPLETED: ['#E5F4E6', '#26752A'],
    CANCELLED: ['#FFE5E5', '#C8333A']
};

const AgendaList = ({ clinic, dateRange, roleUuid, clinicUuid, zone }) => {
    const { user, role } = useAuth();
    const actor = { user, role };
    const { t, i18n } = useTranslation();
    const [search, setSearch] = useState('');
    const [stateFilter, setStateFilter] = useState('active');
    const [clinician, setClinician] = useState('all');
    const [dialog, setDialog] = useState(null);
    const locale = (i18n?.resolvedLanguage || i18n?.language || 'pt').startsWith('pt')
        ? 'pt'
        : 'en';
    const doctor = clinic ? (clinician === 'all' ? undefined : clinician) : roleUuid;
    const query = useQuery({
        queryKey: [
            'clinic-agenda-appointments',
            roleUuid,
            clinicUuid,
            zone,
            dateRange,
            doctor,
            stateFilter
        ],
        queryFn: ({ signal }) =>
            fetchAppointments(actor, {
                doctor,
                statuses: filterStates[stateFilter],
                ...getAgendaBounds(dateRange, zone),
                order: 'ASC',
                allPages: true,
                signal
            }),
        enabled: Boolean(roleUuid),
        staleTime: 30000
    });
    const cliniciansQuery = useQuery({
        queryKey: ['clinic-agenda-clinicians', roleUuid, clinicUuid],
        queryFn: () => getAppointmentClinicians(actor),
        enabled: clinic && Boolean(roleUuid),
        staleTime: 60000
    });
    const groups = useMemo(
        () => groupAppointments(query.data || [], search, dateRange, zone),
        [query.data, search, dateRange, zone]
    );
    const headings = [
        'date',
        'time',
        'patient',
        ...(clinic ? ['clinician'] : []),
        'status',
        'actions'
    ];
    const columns = (
        <colgroup>
            <col style={{ width: 145 }} />
            <col style={{ width: 125 }} />
            <col />
            {clinic && <col />}
            <col style={{ width: 135 }} />
            <col style={{ width: 164 }} />
        </colgroup>
    );

    return (
        <Box sx={{ minWidth: 0 }}>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 2,
                    '& .MuiOutlinedInput-root, & .MuiOutlinedInput-input': {
                        background: '#FFFFFF'
                    }
                }}
            >
                <TextField
                    placeholder={t('agenda.search')}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    inputProps={{ 'aria-label': t('agenda.search') }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="primary" />
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        flex: '1 1 220px',
                        '& .MuiOutlinedInput-root': { bgcolor: '#FFFFFF' }
                    }}
                />
                {clinic && (
                    <TextField
                        select
                        value={clinician}
                        onChange={(event) => setClinician(event.target.value)}
                        disabled={cliniciansQuery.isPending || cliniciansQuery.isError}
                        sx={{ flex: '0 1 190px', minWidth: 160, bgcolor: '#FFFFFF' }}
                        SelectProps={{
                            inputProps: { 'aria-label': t('agenda.clinicians') }
                        }}
                    >
                        <MenuItem value="all">{t('agenda.clinicians')}</MenuItem>
                        {(cliniciansQuery.data || []).map((person) => (
                            <MenuItem key={person.role_uuid} value={person.role_uuid}>
                                {person.name}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                <TextField
                    select
                    value={stateFilter}
                    onChange={(event) => setStateFilter(event.target.value)}
                    sx={{ flex: '0 0 260px', minWidth: 260 }}
                    SelectProps={{ inputProps: { 'aria-label': t('agenda.status') } }}
                >
                    <MenuItem value="active">{t('agenda.scheduledOngoing')}</MenuItem>
                    <MenuItem value="completed">{t('agenda.completed')}</MenuItem>
                    <MenuItem value="cancelled">{t('agenda.cancelled')}</MenuItem>
                </TextField>
            </Box>
            {clinic && cliniciansQuery.isError && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button onClick={() => cliniciansQuery.refetch()}>
                            {t('agenda.retry')}
                        </Button>
                    }
                >
                    {t('agenda.cliniciansError')}
                </Alert>
            )}
            {query.isError && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button onClick={() => query.refetch()}>
                            {t('agenda.retry')}
                        </Button>
                    }
                >
                    {t('agenda.loadError')}
                </Alert>
            )}
            <TableContainer
                component={Box}
                sx={{ minWidth: 0, maxWidth: '100%', overflowX: 'auto' }}
            >
                <Box sx={{ minWidth: clinic ? 790 : 650 }}>
                    <Table
                        aria-hidden="true"
                        sx={{
                            tableLayout: 'fixed',
                            mb: 2,
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            '& th:first-of-type': { borderRadius: '6px 0 0 6px' },
                            '& th:last-of-type': { borderRadius: '0 6px 6px 0' }
                        }}
                    >
                        {columns}
                        <TableHead
                            sx={{
                                '& .MuiTableCell-root': {
                                    bgcolor: 'primary.main',
                                    color: '#FFFFFF',
                                    py: 1.5,
                                    fontWeight: 500,
                                    borderBottom: 0
                                }
                            }}
                        >
                            <TableRow>
                                {headings.map((heading) => (
                                    <TableCell
                                        key={heading}
                                        align={
                                            ['status', 'actions'].includes(heading)
                                                ? 'center'
                                                : 'left'
                                        }
                                    >
                                        {heading === 'status'
                                            ? null
                                            : t(`agenda.${heading}`)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                    </Table>
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        <Table
                            aria-label={t('agenda.appointmentList')}
                            sx={{ minWidth: clinic ? 790 : 650, tableLayout: 'fixed' }}
                        >
                            {columns}
                            <TableHead
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: 1,
                                    height: 1,
                                    overflow: 'hidden',
                                    clipPath: 'inset(50%)',
                                    bgcolor: 'primary.main',
                                    '& .MuiTableCell-root': {
                                        color: '#FFFFFF',
                                        py: 1.5,
                                        fontWeight: 600
                                    }
                                }}
                            >
                                <TableRow>
                                    {headings.map((heading) => (
                                        <TableCell
                                            key={heading}
                                            align={
                                                ['status', 'actions'].includes(heading)
                                                    ? 'center'
                                                    : 'left'
                                            }
                                        >
                                            {heading === 'status' ? (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        position: 'absolute',
                                                        width: 1,
                                                        height: 1,
                                                        overflow: 'hidden',
                                                        clipPath: 'inset(50%)'
                                                    }}
                                                >
                                                    {t('agenda.status')}
                                                </Box>
                                            ) : (
                                                t(`agenda.${heading}`)
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {query.isPending ? (
                                    [0, 1, 2, 3].map((row) => (
                                        <TableRow key={row}>
                                            {headings.map((heading) => (
                                                <TableCell key={heading}>
                                                    <Skeleton height={36} />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : query.isError ? null : groups.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={headings.length}
                                            sx={{
                                                height: 240,
                                                textAlign: 'center',
                                                color: 'text.secondary'
                                            }}
                                        >
                                            {t('agenda.empty')}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    groups.flatMap(([date, appointments], groupIndex) =>
                                        appointments.map((appointment, index) => (
                                            <TableRow
                                                key={appointment.uuid}
                                                sx={{
                                                    height: 64,
                                                    '& .MuiTableCell-root': {
                                                        borderBottom: 0,
                                                        borderTop:
                                                            index === 0 && groupIndex > 0
                                                                ? '1px solid #DCE3E8'
                                                                : 0,
                                                        py: 1.25
                                                    }
                                                }}
                                            >
                                                {index === 0 && (
                                                    <TableCell
                                                        rowSpan={appointments.length}
                                                        sx={{
                                                            verticalAlign: 'top',
                                                            pt: '18px !important'
                                                        }}
                                                    >
                                                        <Typography
                                                            component="h3"
                                                            sx={{
                                                                fontWeight: 700,
                                                                fontSize: '1.125rem',
                                                                textTransform:
                                                                    'capitalize'
                                                            }}
                                                        >
                                                            {dayjs(date)
                                                                .locale(locale)
                                                                .format('D MMMM')}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                textTransform:
                                                                    'capitalize'
                                                            }}
                                                        >
                                                            {dayjs(date)
                                                                .locale(locale)
                                                                .format('dddd')}
                                                        </Typography>
                                                    </TableCell>
                                                )}
                                                <TableCell
                                                    sx={{
                                                        whiteSpace: 'nowrap',
                                                        fontSize: '0.8125rem'
                                                    }}
                                                >
                                                    {dayjs(appointment.ts)
                                                        .tz(zone)
                                                        .format('HH:mm')}
                                                    {Number(appointment.slot_duration) >
                                                        0 &&
                                                        ` - ${dayjs(appointment.ts).tz(zone).add(Number(appointment.slot_duration), 'minute').format('HH:mm')}`}
                                                </TableCell>
                                                <TableCell
                                                    sx={{ overflowWrap: 'anywhere' }}
                                                >
                                                    {appointment.patient?.name || '-'}
                                                </TableCell>
                                                {clinic && (
                                                    <TableCell
                                                        sx={{ overflowWrap: 'anywhere' }}
                                                    >
                                                        {appointment.doctor?.name || '-'}
                                                    </TableCell>
                                                )}
                                                <TableCell align="center">
                                                    <Chip
                                                        size="small"
                                                        label={t(appointment.status === 'SCHEDULED' ? 'Agendado' :
                                                            `agenda.${statusLabels[appointment.status] || 'status'}`
                                                        )}
                                                        sx={{
                                                            bgcolor:
                                                                colors[
                                                                    appointment.status
                                                                ]?.[0],
                                                            color: colors[
                                                                appointment.status
                                                            ]?.[1],
                                                            fontWeight: 700,
                                                            fontSize: '0.75rem',
                                                            minWidth: 100
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75 }}>
                                                        <Tooltip title={t('agenda.reschedule')}>
                                                            <span><IconButton aria-label={t('agenda.reschedule')}
                                                                disabled={appointment.status !== 'SCHEDULED'}
                                                                onClick={() => setDialog({ appointment, mode: 'reschedule' })}
                                                                sx={{ color: '#fff', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}>
                                                                <EventRepeat />
                                                            </IconButton></span>
                                                        </Tooltip>
                                                        <Tooltip title={t('Cancelar agendamento')}>
                                                            <span><IconButton aria-label={t('Cancelar agendamento')}
                                                                disabled={appointment.status !== 'SCHEDULED'}
                                                                onClick={() => setDialog({ appointment, mode: 'cancel' })}
                                                                sx={{ color: 'accent.main', border: '1px solid', borderColor: 'accent.main' }}>
                                                                <EventBusyOutlined />
                                                            </IconButton></span>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )
                                )}
                            </TableBody>
                        </Table>
                    </Paper>
                </Box>
            </TableContainer>
            {dialog && (
                <AppointmentDialogs
                    key={`${dialog.appointment.uuid}-${dialog.mode}`}
                    {...dialog}
                    zone={zone}
                    onClose={() => setDialog(null)}
                />
            )}
        </Box>
    );
};

AgendaList.propTypes = {
    clinic: PropTypes.bool.isRequired,
    dateRange: PropTypes.shape({
        start: PropTypes.string.isRequired,
        end: PropTypes.string.isRequired
    }).isRequired,
    roleUuid: PropTypes.string,
    clinicUuid: PropTypes.string,
    zone: PropTypes.string.isRequired,
};

export default AgendaList;
